import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import {
  gtmPushData,
  GetBackgroundStyle,
  IsNodeStructure,
} from "@talladega/helpers";
import InstantCashOfferHeading from "@modules/purchase/components/offer/InstantCashOfferHeading";
import {
  ContentAreaLayout,
  FaqLayout,
  ActionButton,
  FeatureBannerLayout,
  GravityFormInput,
  GravityFormLayout,
  EmbeddedVideoPlayerLayout,
  IFrameLayout,
  CmsButtonType,
  FullWidthCTABannerLayout,
  MultiCardSectionLayout,
  ReviewsCarouselLayout,
  GalleryAndContentLayout,
  GalleryAndContentCarouselLayout,
} from "@talladega/types";
import {
  CmsHeading,
  ContentArea,
  EmbeddedVideoPlayer,
  Faq,
  FeatureBanner,
  FullWidthCTABanner,
  IFrame,
  MultiStepSection,
  MultiStepLayout,
  HeroHeading,
  HeroHeadingLayout,
  ReviewsCarousel,
  GalleryAndContent,
  GalleryAndContentCarousel,
} from "@talladega/cms";
import { ArrowRightIcon } from "@talladega/icons";
import CloudflareImagesImage from "@common/components/CloudflareImagesImage";
import classNames from "classnames";
import { CmsLayout } from "../types/CmsLayout";
import { ContentHeaderLayout } from "../types/ContentHeaderLayout";
import { ContentBlockFlexLayout } from "../types/ContentBlockFlexLayout";
import GravityForm from "./GravityForm";
import { InstantCashOfferHeadingLayout } from "../types/InstantCashOfferHeadingLayout";
import { HeroHeadingFormLayout } from "../types/HeroHeadingFormLayout";
import HeroHeadingForm from "./HeroHeadingForm";
import CmsButton from "./CmsButton";
import CmsButtonModalContainer from "./CmsButtonModalContainer";
import MultiCardSection from "./MultiCardSection";

type Props = {
  layouts: CmsLayout[];
  dealerId?: string;
};

const CmsLayouts = ({ layouts, dealerId }: Props) => {
  const htmlIdRef = useRef<HTMLDivElement | null>(null);
  const [urlHtmlId, setUrlHtmlId] = useState<string | null>(null);
  const router = useRouter();

  useEffect(() => {
    if (router.asPath.includes("#") && !urlHtmlId) {
      setUrlHtmlId(router.asPath ? router.asPath.split("#")[1] : null);
    }
    if (htmlIdRef.current && urlHtmlId) {
      htmlIdRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [router.asPath, urlHtmlId]);

  const baseDomain = process.env.NEXT_PUBLIC_CLOUDFLARE_IMAGES_DOMAIN || "";

  const getLayoutComponent = (layout: CmsLayout) => {
    switch (layout?.typeName) {
      case "DefaultFlexHeroHeadingLayout": {
        const heroHeadingProps = layout as unknown as HeroHeadingLayout;

        const renderCmsButton = (
          setButtonModalState: React.Dispatch<
            React.SetStateAction<{ show: boolean; actionType: ActionButton }>
          >,
          button: CmsButtonType
        ) => {
          return (
            <CmsButton
              className="w-full"
              button={button}
              rightIcon={<ArrowRightIcon />}
              setButtonModalState={setButtonModalState}
              htmlIdRef={htmlIdRef}
            />
          );
        };

        const renderCmsHeroImage = ({
          src,
          alt,
          width,
          height,
        }: {
          src?: string;
          alt?: string;
          width?: number;
          height?: number;
        }) => {
          if (!src) return null;

          return (
            <CloudflareImagesImage
              src={src}
              alt={alt ?? ""}
              width={width ?? 0}
              height={height ?? 0}
              fill
              className="z-[2] h-auto w-full rounded-lg object-contain"
            />
          );
        };

        const renderCmsModal = (buttonModalState: {
          show: boolean;
          actionType: ActionButton;
        }) => {
          return (
            <CmsButtonModalContainer buttonModalState={buttonModalState} />
          );
        };

        return (
          <div
            ref={heroHeadingProps.htmlId === urlHtmlId ? htmlIdRef : undefined}
          >
            <HeroHeading
              heroHeading={heroHeadingProps.heroHeading}
              heroSubheading={heroHeadingProps.heroSubheading}
              button={heroHeadingProps.button}
              heroImage={heroHeadingProps.heroImage}
              imagePosition={heroHeadingProps.imagePosition}
              background={heroHeadingProps.background}
              htmlIdRef={htmlIdRef}
              cmsButton={renderCmsButton}
              cmsButtonModalContainer={renderCmsModal}
              cmsHeroImage={renderCmsHeroImage}
              baseDomain={baseDomain}
            />
          </div>
        );
      }
      case "DefaultFlexHeroHeadingFormLayout": {
        const { htmlId, ...props } = layout as unknown as HeroHeadingFormLayout;

        return (
          <div ref={htmlId === urlHtmlId ? htmlIdRef : undefined}>
            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
            <HeroHeadingForm htmlIdRef={htmlIdRef} {...props} />
          </div>
        );
      }
      case "DefaultFlexGalleryAndContentLayout": {
        const galleryProps = layout as unknown as GalleryAndContentLayout;

        const renderCmsButton = (
          setButtonModalState: React.Dispatch<
            React.SetStateAction<{ show: boolean; actionType: ActionButton }>
          >,
          button: CmsButtonType
        ) => (
          <CmsButton
            button={button}
            rightIcon={<ArrowRightIcon />}
            setButtonModalState={setButtonModalState}
            htmlIdRef={htmlIdRef}
          />
        );

        const renderCmsModal = (buttonModalState: {
          show: boolean;
          actionType: ActionButton;
        }) => <CmsButtonModalContainer buttonModalState={buttonModalState} />;

        const renderCloudflareImage = ({
          src,
          alt,
          className,
          fill,
          width,
          height,
        }: {
          src?: string;
          alt?: string;
          className?: string;
          fill?: boolean;
          width?: number;
          height?: number;
        }) => {
          if (!src) return null;

          return (
            <CloudflareImagesImage
              src={src}
              alt={alt ?? ""}
              className={className}
              fill={fill}
              width={width}
              height={height}
            />
          );
        };

        return (
          <div
            ref={
              galleryProps.galleryAndContentGroup.htmlId === urlHtmlId
                ? htmlIdRef
                : undefined
            }
          >
            <GalleryAndContent
              layout={galleryProps}
              cmsButton={renderCmsButton}
              cmsButtonModalContainer={renderCmsModal}
              cloudflareImage={renderCloudflareImage}
              baseDomain={baseDomain}
            />
          </div>
        );
      }
      case "DefaultFlexGalleryAndContentCarouselLayout": {
        const galleryProps =
          layout as unknown as GalleryAndContentCarouselLayout;

        const renderCmsButton = (
          setButtonModalState: React.Dispatch<
            React.SetStateAction<{ show: boolean; actionType: ActionButton }>
          >,
          button: CmsButtonType
        ) => (
          <CmsButton
            button={button}
            rightIcon={<ArrowRightIcon />}
            setButtonModalState={setButtonModalState}
            htmlIdRef={htmlIdRef}
          />
        );

        const renderCmsModal = (buttonModalState: {
          show: boolean;
          actionType: ActionButton;
        }) => <CmsButtonModalContainer buttonModalState={buttonModalState} />;

        const renderCloudflareImage = ({
          src,
          alt,
          className,
          fill,
          width,
          height,
        }: {
          src?: string;
          alt?: string;
          className?: string;
          fill?: boolean;
          width?: number;
          height?: number;
        }) => {
          if (!src) return null;

          return (
            <CloudflareImagesImage
              src={src}
              alt={alt ?? ""}
              className={className}
              fill={fill}
              width={width}
              height={height}
            />
          );
        };

        return (
          <GalleryAndContentCarousel
            galleryAndContentRepeater={galleryProps?.galleryAndContentRepeater}
            urlHtmlId={urlHtmlId}
            htmlIdRef={htmlIdRef}
            cmsButton={renderCmsButton}
            cmsButtonModalContainer={renderCmsModal}
            cloudflareImage={renderCloudflareImage}
            baseDomain={baseDomain}
          />
        );
      }
      case "DefaultFlexMultiCardSectionLayout": {
        const multiCardSection = layout as unknown as MultiCardSectionLayout;
        return (
          <div
            ref={multiCardSection.htmlId === urlHtmlId ? htmlIdRef : undefined}
          >
            <MultiCardSection
              cardLayout={multiCardSection.cardLayout}
              button={multiCardSection.button}
              background={multiCardSection.background}
              htmlIdRef={htmlIdRef}
              sectionHeading={multiCardSection.sectionHeading}
              sectionSubheading={multiCardSection.sectionSubheading}
            />
          </div>
        );
      }
      case "DefaultFlexReviewsCarouselLayout": {
        const reviewsCarousel = layout as unknown as ReviewsCarouselLayout;
        return (
          <div
            ref={reviewsCarousel.htmlId === urlHtmlId ? htmlIdRef : undefined}
          >
            <ReviewsCarousel
              heading={reviewsCarousel.heading}
              subheading={reviewsCarousel.subheading}
              carouselContentList={reviewsCarousel.carouselContentList}
              background={reviewsCarousel.background}
              baseDomain={baseDomain}
            />
          </div>
        );
      }
      case "DefaultFlexFullWidthCtaBannerLayout": {
        const ctaBanner = layout as unknown as FullWidthCTABannerLayout;

        const renderCmsButton = (
          setButtonModalState: React.Dispatch<
            React.SetStateAction<{ show: boolean; actionType: ActionButton }>
          >,
          button: CmsButtonType
        ) => {
          return (
            <CmsButton
              className="w-full"
              button={button}
              rightIcon={<ArrowRightIcon />}
              setButtonModalState={setButtonModalState}
              htmlIdRef={htmlIdRef}
            />
          );
        };

        const renderCtaIconImage = (
          index: number,
          {
            src,
            alt,
            width,
            height,
          }: {
            src?: string;
            alt?: string;
            width?: number;
            height?: number;
          }
        ) => {
          if (!src) return null;

          return (
            <CloudflareImagesImage
              key={`cta-icon-${index}`}
              src={src}
              alt={alt ?? ""}
              width={width ?? 0}
              height={height ?? 0}
              style={{ display: "flex", flexShrink: 0 }}
            />
          );
        };

        const renderCmsModal = (buttonModalState: {
          show: boolean;
          actionType: ActionButton;
        }) => {
          return (
            <CmsButtonModalContainer buttonModalState={buttonModalState} />
          );
        };

        return (
          <FullWidthCTABanner
            layout={ctaBanner}
            cmsButton={renderCmsButton}
            ctaIconImage={renderCtaIconImage}
            cmsButtonModalContainer={renderCmsModal}
            baseDomain={baseDomain}
          />
        );
      }
      case "DefaultFlexGravityFormLayout": {
        const gravityFormProps = layout as unknown as GravityFormLayout;

        return gravityFormProps.form ? (
          <div
            ref={gravityFormProps.htmlId === urlHtmlId ? htmlIdRef : undefined}
          >
            <GravityForm
              form={gravityFormProps.form}
              formId={gravityFormProps.id}
              heading={gravityFormProps.heading}
              subheading={gravityFormProps.subheading}
              vinsolutionsId={gravityFormProps.vinsolutionsId}
              campaignTag={gravityFormProps.campaignTag}
              backgroundImageGroup={gravityFormProps.backgroundImageGroup}
              background={gravityFormProps.background}
              confirmationText={
                gravityFormProps.form.confirmations?.[0].message
              }
              submitButtonText={gravityFormProps.form.submitButton?.text}
              onSubmit={(formInput: GravityFormInput) => {
                // Temporary comment out gtmPush.
                // eslint-disable-next-line no-console
                console.log("Submitted", formInput);

                if (dealerId) {
                  gtmPushData({
                    event: "Locations",
                    element: "Form",
                    location: dealerId,
                    descriptor: "Submit",
                    firstName: formInput.firstName,
                    lastName: formInput.lastName,
                    email: formInput.email,
                    phone: formInput.phoneNumber,
                  });
                } else if (router.asPath.startsWith("/about")) {
                  gtmPushData({
                    event: "About-Us",
                    descriptor: "Submit",
                    firstName: formInput.firstname,
                    lastName: formInput.lastname,
                    email: formInput.email,
                    phone: formInput.phoneNumber,
                  });
                }
              }}
            />
          </div>
        ) : null;
      }
      case "DefaultFlexFaqLayout": {
        const faqProps = layout as unknown as FaqLayout;
        const filteredFaqSections = faqProps.faqSections.filter(
          (x) => x.faqSectionHeading != null
        );

        return (
          <div ref={faqProps.htmlId === urlHtmlId ? htmlIdRef : undefined}>
            <div
              id="cms-frequently-asked-questions"
              className="no-scrollbar relative overflow-visible bg-white bg-cover bg-center"
              style={GetBackgroundStyle(faqProps.background, baseDomain)}
            >
              <Faq
                heading={faqProps.heading}
                subheading={faqProps.subheading}
                faqSections={filteredFaqSections}
                background={faqProps.background}
              />
            </div>
          </div>
        );
      }
      case "DefaultFlexIframeLayout": {
        const iframeProps = layout as unknown as IFrameLayout;
        return (
          <div ref={iframeProps.htmlId === urlHtmlId ? htmlIdRef : undefined}>
            <IFrame
              heading={iframeProps.heading}
              subheading={iframeProps.subheading}
              iframeSrc={iframeProps.iframeSrc}
              background={iframeProps.background}
              baseDomain={baseDomain}
            />
          </div>
        );
      }
      case "DefaultFlexContentBlockFlexLayout": {
        const contentBlockProps = layout as unknown as ContentBlockFlexLayout;

        return (
          <div
            className="no-scrollbar relative overflow-visible bg-white bg-cover bg-center"
            style={GetBackgroundStyle(contentBlockProps.background, baseDomain)}
          >
            <div className="mx-auto max-w-page px-4 pb-6 pt-10 lg:px-5 lg:py-16">
              <CmsLayouts
                layouts={contentBlockProps.contentBlock as CmsLayout[]}
              />
            </div>
          </div>
        );
      }

      case "DefaultFlexContentBlockContentHeadingLayout": {
        const { heading } = layout as unknown as ContentHeaderLayout;

        return heading ? (
          <CmsHeading
            htmlId={heading.headingGroup.htmlId}
            headingLevel={heading.headingGroup.headingLevel}
            text={heading.headingGroup.text}
            textSize={heading.headingGroup.textSize}
          />
        ) : null;
      }

      case "DefaultFlexContentBlockContentAreaLayout": {
        const contentAreaProps = layout as unknown as ContentAreaLayout;
        return (
          <div
            ref={contentAreaProps.htmlId === urlHtmlId ? htmlIdRef : undefined}
          >
            <ContentArea contentDetails={contentAreaProps.contentDetails} />
          </div>
        );
      }

      case "DefaultFlexContentBlockGalleryAndContentLayout": {
        const galleryProps = layout as unknown as GalleryAndContentLayout;

        const renderCmsButton = (
          setButtonModalState: React.Dispatch<
            React.SetStateAction<{ show: boolean; actionType: ActionButton }>
          >,
          button: CmsButtonType
        ) => (
          <CmsButton
            button={button}
            rightIcon={<ArrowRightIcon />}
            setButtonModalState={setButtonModalState}
            htmlIdRef={htmlIdRef}
          />
        );

        const renderCmsModal = (buttonModalState: {
          show: boolean;
          actionType: ActionButton;
        }) => <CmsButtonModalContainer buttonModalState={buttonModalState} />;

        const renderCloudflareImage = ({
          src,
          alt,
          className,
          fill,
          width,
          height,
        }: {
          src?: string;
          alt?: string;
          className?: string;
          fill?: boolean;
          width?: number;
          height?: number;
        }) => {
          if (!src) return null;

          return (
            <CloudflareImagesImage
              src={src}
              alt={alt ?? ""}
              className={className}
              fill={fill}
              width={width}
              height={height}
            />
          );
        };

        return (
          <div
            ref={
              galleryProps.galleryAndContentGroup.htmlId === urlHtmlId
                ? htmlIdRef
                : undefined
            }
          >
            <GalleryAndContent
              layout={galleryProps}
              cmsButton={renderCmsButton}
              cmsButtonModalContainer={renderCmsModal}
              cloudflareImage={renderCloudflareImage}
              baseDomain={baseDomain}
            />
          </div>
        );
      }

      case "DefaultFlexContentBlockFullWidthCtaBannerLayout": {
        const ctaBanner = layout as unknown as FullWidthCTABannerLayout;

        const renderCmsButton = (
          setButtonModalState: React.Dispatch<
            React.SetStateAction<{ show: boolean; actionType: ActionButton }>
          >,
          button: CmsButtonType
        ) => {
          return (
            <CmsButton
              className="w-full"
              button={button}
              rightIcon={<ArrowRightIcon />}
              setButtonModalState={setButtonModalState}
              htmlIdRef={htmlIdRef}
            />
          );
        };

        const renderCtaIconImage = (
          index: number,
          {
            src,
            alt,
            width,
            height,
          }: {
            src?: string;
            alt?: string;
            width?: number;
            height?: number;
          }
        ) => {
          if (!src) return null;

          return (
            <CloudflareImagesImage
              key={`cta-icon-${index}`}
              src={src}
              alt={alt ?? ""}
              width={width ?? 0}
              height={height ?? 0}
              style={{ display: "flex", flexShrink: 0 }}
            />
          );
        };

        const renderCmsModal = (buttonModalState: {
          show: boolean;
          actionType: ActionButton;
        }) => {
          return (
            <CmsButtonModalContainer buttonModalState={buttonModalState} />
          );
        };

        return (
          <FullWidthCTABanner
            layout={ctaBanner}
            cmsButton={renderCmsButton}
            ctaIconImage={renderCtaIconImage}
            cmsButtonModalContainer={renderCmsModal}
            baseDomain={baseDomain}
          />
        );
      }

      case "DefaultFlexContentBlockEmbeddedVideoLayout": {
        const embeddedVideo = layout as unknown as EmbeddedVideoPlayerLayout;
        return (
          <div ref={embeddedVideo.htmlId === urlHtmlId ? htmlIdRef : undefined}>
            <EmbeddedVideoPlayer contentVideo={embeddedVideo.contentVideo} />
          </div>
        );
      }

      case "DefaultFlexMultiStepSectionLayout": {
        const multiStep = layout as unknown as MultiStepLayout;

        const renderCmsButton = (
          setButtonModalState: React.Dispatch<
            React.SetStateAction<{ show: boolean; actionType: ActionButton }>
          >,
          button: CmsButtonType
        ) => {
          return (
            <CmsButton
              className="w-full"
              button={button}
              rightIcon={<ArrowRightIcon />}
              setButtonModalState={setButtonModalState}
              htmlIdRef={htmlIdRef}
            />
          );
        };

        const renderCmsIconImage = (
          index: number,
          {
            src,
            alt,
            width,
            height,
          }: {
            src?: string;
            alt?: string;
            width?: number;
            height?: number;
          }
        ) => {
          if (!src) return null;

          return (
            <CloudflareImagesImage
              key={`cta-icon-${index}`}
              src={src}
              alt={alt ?? ""}
              width={width ?? 0}
              height={height ?? 0}
            />
          );
        };

        const renderCmsModal = (buttonModalState: {
          show: boolean;
          actionType: ActionButton;
        }) => {
          return (
            <CmsButtonModalContainer buttonModalState={buttonModalState} />
          );
        };

        return (
          <div ref={multiStep.htmlId === urlHtmlId ? htmlIdRef : undefined}>
            <MultiStepSection
              stepLayout={multiStep.stepLayout}
              sectionHeading={multiStep.sectionHeading}
              sectionSubheading={multiStep.sectionSubheading}
              button={multiStep.button}
              background={multiStep.background}
              htmlIdRef={htmlIdRef}
              cmsButton={renderCmsButton}
              cmsButtonModalContainer={renderCmsModal}
              cmsIconImage={renderCmsIconImage}
              baseDomain={baseDomain}
            />
          </div>
        );
      }

      case "DefaultFlexFeatureBannerLayout": {
        const featureBannerLayout = layout as unknown as FeatureBannerLayout;

        const renderCmsButton = (
          setButtonModalState: React.Dispatch<
            React.SetStateAction<{ show: boolean; actionType: ActionButton }>
          >
        ) => {
          return (
            <CmsButton
              button={featureBannerLayout.container.button}
              rightIcon={<ArrowRightIcon />}
              setButtonModalState={setButtonModalState}
              className="w-full"
            />
          );
        };

        const renderBgImage = (isInView: boolean) => {
          const { sourceUrl, altText } = IsNodeStructure(
            featureBannerLayout.backgroundImage
          )
            ? featureBannerLayout.backgroundImage.node
            : featureBannerLayout.backgroundImage ?? {};

          if (!sourceUrl) return null;

          return (
            <CloudflareImagesImage
              src={sourceUrl}
              alt={altText ?? ""}
              fill
              className={classNames(
                "absolute inset-0 h-full w-full bg-center object-cover",
                {
                  "rounded-xl": !isInView,
                }
              )}
            />
          );
        };

        const renderIconImage = () => {
          const { sourceUrl, altText } = IsNodeStructure(
            featureBannerLayout.container.icon
          )
            ? featureBannerLayout.container.icon.node
            : featureBannerLayout.container.icon ?? {};

          if (!sourceUrl) return null;

          return (
            <CloudflareImagesImage src={sourceUrl} alt={altText ?? ""} fill />
          );
        };

        const renderCmsModal = (buttonModalState: {
          show: boolean;
          actionType: ActionButton;
        }) => {
          return (
            <CmsButtonModalContainer buttonModalState={buttonModalState} />
          );
        };

        return (
          <FeatureBanner
            layout={featureBannerLayout}
            cmsButton={(setButtonModalState) =>
              renderCmsButton(setButtonModalState)
            }
            bgCloudflareImage={(isInView) => renderBgImage(isInView)}
            iconCloudflareImage={renderIconImage()}
            cmsButtonModalContainer={renderCmsModal}
          />
        );
      }

      case "DefaultFlexInstantCashOfferHeadingLayout": {
        const icoHeadingLayout =
          layout as unknown as InstantCashOfferHeadingLayout;

        return <InstantCashOfferHeading layout={icoHeadingLayout} />;
      }

      default:
        return null;
    }
  };

  return (
    <>
      {layouts.map((layout, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={`${layout?.typeName}-${i}`}>{getLayoutComponent(layout)}</div>
      ))}
    </>
  );
};

export default CmsLayouts;
